import { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Search } from 'lucide-react'
import { toast } from 'components/ui/toast-store'
import { initializeActiveLocations } from 'slices/locationSlice'
import {
	fetchAllSites,
	fetchSites,
	createSite,
	linkSiteToEvaluation,
} from 'slices/evaluationSiteSlice'
import { Avatar, AvatarImage, AvatarFallback } from 'components/ui/avatar'
import { Button } from 'components/ui/button'
import { Spinner } from 'components/ui/spinner'
import { cn } from 'lib/utils'
import { siteFromLocation } from '../utils/siteFromLocation'
import AltRetailerLogo from 'images/Old_Navy_Logo.png'

interface EvaluationAddExistingSitePopupProps {
	evalId: string
}

type RowSource = 'LOCATION' | 'SITE'

interface PickerRow {
	key: string
	source: RowSource
	id: string
	name: string
	city?: string
	state?: string
	postalCode?: string
	address?: string
	image?: string
	raw: any
}

/**
 * Pick an existing place into this evaluation.
 *
 * Lists two things in one list: catalogue Locations, and sites the
 * operator already built in other evaluations. They are different
 * collections with different add actions, so each row carries a badge
 * saying where it came from. Layout and interaction deliberately mirror
 * `screens/portfolios/popups/PortfolioAddExistingLocationPopup.js` — same
 * search box, same per-row Add button, no checkboxes and no footer.
 */
const EvaluationAddExistingSitePopup = ({ evalId }: EvaluationAddExistingSitePopupProps) => {
	const dispatch = useDispatch()

	const [searchValue, setSearchValue] = useState('')
	const [pendingId, setPendingId] = useState<string | null>(null)

	// Selected one slice of state at a time. Returning an object literal (or a
	// freshly mapped array) from useSelector would produce a new reference on
	// every store action, re-rendering this list constantly and defeating the
	// useMemo below.
	const locationIds: string[] = useSelector((state: any) => state.locations.ids)
	const locationEntities = useSelector((state: any) => state.locations.entities)
	const locationsLoading = useSelector((state: any) => !state.locations.isInitialized)

	const siteEntities = useSelector((state: any) => state.evaluationSites.entities)
	const evaluationSiteIds: string[] = useSelector(
		(state: any) => state.evaluationSites.byEvaluation?.[evalId],
	)
	const sitesLoading = useSelector(
		(state: any) => !state.evaluationSites.allSitesInitialized,
	)

	useEffect(() => {
		dispatch(initializeActiveLocations() as any)
		dispatch(fetchAllSites() as any)
	}, [dispatch])

	const rows = useMemo<PickerRow[]>(() => {
		const locations: any[] = (locationIds || []).map((id) => locationEntities?.[id])
		const allSites: any[] = Object.values(siteEntities || {})
		const linkedSiteIds = new Set((evaluationSiteIds || []).map(String))

		// Sites already in this evaluation are not offerable. Everything else
		// in the operator's Site Library is.
		const siteRows: PickerRow[] = allSites
			.filter((s) => s && !s.isDeleted && !linkedSiteIds.has(String(s._id)))
			.map((s) => ({
				key: `site:${s._id}`,
				source: 'SITE' as const,
				id: String(s._id),
				name: s.name || 'Unnamed site',
				city: s.city,
				state: s.state,
				postalCode: s.postalCode,
				address: s.address,
				// `location` is populated on eval-scoped reads and a bare id on
				// the owner-scoped one, so fall back to the site's own photo.
				image: s.location?.image || s.coverImage || s.imageUrls?.[0],
				raw: s,
			}))

		// A Location already represented by a site in THIS evaluation is
		// already here — same rule the portfolio sync diff uses.
		const representedLocationIds = new Set(
			allSites
				.filter((s) => s?.location && linkedSiteIds.has(String(s._id)))
				.map((s) => String(s.location?._id ?? s.location)),
		)

		// A Location that one of the offerable Site rows already points at
		// would otherwise show up twice for one real place. Keep the site
		// row: it is the richer, evaluation-native object.
		const locationIdsCoveredBySiteRows = new Set(
			siteRows
				.filter((r) => r.raw?.location)
				.map((r) => String(r.raw.location?._id ?? r.raw.location)),
		)

		const locationRows: PickerRow[] = locations
			.filter(
				(loc: any) =>
					loc &&
					!representedLocationIds.has(String(loc._id)) &&
					!locationIdsCoveredBySiteRows.has(String(loc._id)),
			)
			.map((loc: any) => ({
				key: `location:${loc._id}`,
				source: 'LOCATION' as const,
				id: String(loc._id),
				name: loc.assetName || 'Unnamed location',
				city: loc.city,
				state: loc.stateProvince,
				postalCode: loc.postalCode,
				address: loc.address,
				image: loc.image,
				raw: loc,
			}))

		const merged = [...siteRows, ...locationRows]

		const lower = searchValue.trim().toLowerCase()
		const filtered = lower
			? merged.filter((r) =>
					[r.name, r.address, r.city, r.state, r.postalCode]
						.filter(Boolean)
						.some((field) => String(field).toLowerCase().includes(lower)),
				)
			: merged

		return filtered.sort((a, b) => a.name.localeCompare(b.name))
	}, [siteEntities, evaluationSiteIds, locationIds, locationEntities, searchValue])

	const addRow = async (row: PickerRow) => {
		setPendingId(row.key)
		try {
			const action: any =
				row.source === 'SITE'
					? await (dispatch as any)(linkSiteToEvaluation({ siteId: row.id, evalId }))
					: await (dispatch as any)(
							createSite({ evalId, payload: siteFromLocation(row.raw) }),
						)

			if (action?.meta?.requestStatus === 'rejected') {
				toast.error(`Could not add ${row.name}.`)
				return
			}

			// Refetch rather than trusting the merged response. linkSite returns
			// the raw site, whose `location` is a bare ObjectId — the Sites tab
			// reads `site.location?.image` for the card avatar and would fall
			// back to a letter until the next load. Same reason the tab refetches
			// after a portfolio sync.
			await (dispatch as any)(fetchSites(evalId))
		} finally {
			setPendingId(null)
		}
	}

	const isLoading = locationsLoading || sitesLoading

	return (
		<div className="w-full">
			{/* Search */}
			<div className="relative mb-4">
				<Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
				<input
					type="text"
					placeholder="Search by name, address, city, state or postal code..."
					value={searchValue}
					onChange={(e) => setSearchValue(e.target.value)}
					className="w-full rounded-lg border border-gray-200 bg-white py-2 pl-10 pr-4 text-sm text-gray-900 placeholder:text-gray-400 focus:border-[#0080fe] focus:outline-none focus:ring-1 focus:ring-[#0080fe]"
				/>
			</div>

			{isLoading ? (
				<div className="py-8">
					<Spinner />
				</div>
			) : rows.length === 0 ? (
				<div className="py-12 text-center text-sm text-gray-400">
					{searchValue
						? 'Nothing matches your search.'
						: 'No existing locations or sites available to add.'}
				</div>
			) : (
				<div className="max-h-[420px] divide-y divide-gray-100 overflow-y-auto">
					{rows.map((row) => (
						<div key={row.key} className="flex items-center gap-3 py-3">
							<Avatar className="h-9 w-9 flex-shrink-0">
								<AvatarImage src={row.image} alt={row.name} />
								<AvatarFallback>
									<img src={AltRetailerLogo} className="h-full w-full object-cover" alt="" />
								</AvatarFallback>
							</Avatar>

							<span className="min-w-0 truncate text-sm font-medium text-gray-900">
								{row.name}
							</span>

							<span
								className={cn(
									'flex-shrink-0 rounded-full px-2 py-0.5 text-[10px] font-medium',
									row.source === 'LOCATION'
										? 'bg-[#0080fe]/10 text-[#0080fe]'
										: 'bg-gray-100 text-gray-700',
								)}
							>
								{row.source === 'LOCATION' ? 'Catalogue' : 'Site Library'}
							</span>

							<span className="min-w-0 truncate text-sm text-gray-500">
								{[row.city, row.state].filter(Boolean).join(', ')}
							</span>
							<span className="min-w-0 truncate text-sm text-gray-400">{row.postalCode}</span>

							<div className="ml-auto flex-shrink-0">
								<Button
									variant="primary"
									isOutline
									className="text-xs"
									disabled={pendingId != null}
									onClick={() => addRow(row)}
								>
									{pendingId === row.key ? 'Adding...' : 'Add Site'}
								</Button>
							</div>
						</div>
					))}
				</div>
			)}
		</div>
	)
}

export default EvaluationAddExistingSitePopup
